import { ChevronDown, ChevronRight, Code2, CreditCard, Download, RotateCw } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useAdminTransactionsGetAdminTransactions,
  useAdminTransactionsGetAdminTransactionsIdGuidPayload,
} from '@/lib/api/admin-transactions/admin-transactions';
import type { TransactionPayloadDto, TransactionSummaryDto } from '@/lib/api/schemas';
import { useHasRole } from '@/lib/auth/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CopyIcon } from '@/components/CopyIcon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { formatBytes, tryPrettyJson } from '@/lib/json/pretty';
import { cn } from '@/lib/utils';

interface Filters {
  status?: number;
  endpoint?: string;
  pspReference?: string;
  correlationId?: string;
  from?: string;
  to?: string;
  merchantId?: string;
  storeId?: string;
}

export default function TransactionsPage() {
  const [filters, setFilters] = useState<Filters>({});
  const [pendingFilters, setPendingFilters] = useState<Filters>({});
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useAdminTransactionsGetAdminTransactions({
    page,
    limit: 25,
    status: filters.status,
    endpoint: filters.endpoint || undefined,
    pspReference: filters.pspReference || undefined,
    correlationId: filters.correlationId || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    merchantId: filters.merchantId || undefined,
    storeId: filters.storeId || undefined,
  });

  const pages = useMemo(() => {
    if (!query.data) return 1;
    const total = Number(query.data.total);
    const limit = Number(query.data.limit) || 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [query.data]);

  const apply = () => {
    setFilters(pendingFilters);
    setPage(1);
  };
  const reset = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  return (
    <section className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Transactions</h1>
          <p className="text-sm text-muted-foreground">
            Every proxy request / response pair. Bodies are stored as SHA-256 hashes only.
          </p>
        </div>
        <Button variant="outline" className="gap-2" disabled title="CSV export ships with Phase 10b (backend streaming endpoint).">
          <Download className="h-4 w-4" />
          Export CSV (soon)
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Press Apply to refresh — all filters combine with AND.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <FilterField label="Status code">
            <Select
              value={pendingFilters.status ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setPendingFilters((f) => ({ ...f, status: v ? Number(v) : undefined }));
              }}
            >
              <option value="">Any</option>
              <option value="200">200 OK</option>
              <option value="400">400 Bad Request</option>
              <option value="401">401 Unauthorized</option>
              <option value="403">403 Forbidden</option>
              <option value="422">422 Unprocessable Entity</option>
              <option value="500">500 Server Error</option>
              <option value="502">502 Bad Gateway</option>
            </Select>
          </FilterField>
          <FilterField label="Endpoint contains">
            <Input
              placeholder="/proxy/v68/payments"
              value={pendingFilters.endpoint ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, endpoint: e.target.value }))}
            />
          </FilterField>
          <FilterField label="Correlation ID">
            <Input
              placeholder="exact match"
              value={pendingFilters.correlationId ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, correlationId: e.target.value }))}
            />
          </FilterField>
          <FilterField label="PSP reference">
            <Input
              placeholder="881234567890ABCD"
              value={pendingFilters.pspReference ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, pspReference: e.target.value }))}
            />
          </FilterField>
          <FilterField label="From">
            <Input
              type="datetime-local"
              value={pendingFilters.from ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, from: e.target.value }))}
            />
          </FilterField>
          <FilterField label="To">
            <Input
              type="datetime-local"
              value={pendingFilters.to ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, to: e.target.value }))}
            />
          </FilterField>
          <FilterField label="Merchant ID">
            <Input
              placeholder="DealerWorksPOS"
              value={pendingFilters.merchantId ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, merchantId: e.target.value }))}
            />
          </FilterField>
          <FilterField label="Store ID">
            <Input
              placeholder="store_001"
              value={pendingFilters.storeId ?? ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, storeId: e.target.value }))}
            />
          </FilterField>

          <div className="flex items-end gap-2 md:col-span-2 xl:col-span-3">
            <Button onClick={apply} className="gap-2">
              <RotateCw className="h-4 w-4" />
              Apply
            </Button>
            <Button variant="ghost" onClick={reset}>
              Reset
            </Button>
            <div className="ml-auto text-xs text-muted-foreground">
              {query.data ? `${query.data.total} matches` : ' '}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {query.isError ? (
            <div className="p-6 text-sm text-destructive">
              {(query.error as Error)?.message ?? 'Could not load transactions.'}
            </div>
          ) : query.isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : !query.data || query.data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No transactions match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-10 p-3"></th>
                    <th className="p-3 font-medium">Time</th>
                    <th className="p-3 font-medium">Client</th>
                    <th className="p-3 font-medium">Endpoint</th>
                    <th className="p-3 font-medium">Method</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Latency</th>
                    <th className="p-3 font-medium">PSP ref</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {query.data.items.map((tx) => (
                    <Fragment key={tx.id}>
                      <tr
                        className="cursor-pointer hover:bg-muted/30"
                        onClick={() => setExpanded((prev) => (prev === tx.id ? null : tx.id))}
                      >
                        <td className="p-3 align-top text-muted-foreground">
                          {expanded === tx.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="p-3 align-top text-xs tabular-nums">{formatTime(tx.createdAt)}</td>
                        <td className="p-3 align-top">
                          <div className="font-medium">{tx.clientName}</div>
                          <div className="font-mono text-xs text-muted-foreground">{tx.clientExternalId}</div>
                        </td>
                        <td className="p-3 align-top font-mono text-xs">
                          {tx.method} {tx.endpoint}
                        </td>
                        <td className="p-3 align-top">
                          <PaymentMethodBadge type={tx.paymentMethodType ?? null} />
                        </td>
                        <td className="p-3 align-top">
                          <StatusBadge status={Number(tx.statusCode)} />
                        </td>
                        <td className="p-3 align-top tabular-nums">{tx.durationMs} ms</td>
                        <td className="p-3 align-top font-mono text-xs text-muted-foreground">
                          {tx.adyenPspReference ?? '—'}
                        </td>
                      </tr>
                      {expanded === tx.id && (
                        <tr>
                          <td colSpan={8} className="bg-muted/20 p-4">
                            <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-150">
                              <ExpandedDetail tx={tx} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {query.data && query.data.total > query.data.limit && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              Page {page} of {pages} · {query.data.total} total
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function StatusBadge({ status }: { status: number }) {
  if (status >= 200 && status < 300) return <Badge variant="success">{status}</Badge>;
  if (status >= 300 && status < 400) return <Badge variant="outline">{status}</Badge>;
  if (status >= 400 && status < 500) return <Badge variant="warning">{status}</Badge>;
  return <Badge variant="destructive">{status}</Badge>;
}

/**
 * Renders a per-row payment-method chip sourced from
 * `tx.paymentMethodType` (denormalised at ingest from
 * `paymentMethod.type`). Falls back to a muted dash when the request had
 * no body (e.g. `/payments/details`, `/storedPaymentMethods` DELETE).
 */
function PaymentMethodBadge({ type }: { type: string | null }) {
  if (!type) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const lower = type.toLowerCase();
  const label = METHOD_LABELS[lower] ?? type;
  const className = METHOD_CLASSES[lower] ?? METHOD_CLASSES.default;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${className}`}
    >
      <CreditCard className="h-3 w-3" />
      {label}
    </span>
  );
}

// Friendly labels + colour per method type. Defaults keep an unknown
// method renderable without a code change — partners can send anything.
const METHOD_LABELS: Record<string, string> = {
  scheme: 'Card',
  googlepay: 'Google Pay',
  applepay: 'Apple Pay',
  ideal: 'iDEAL',
  paypal: 'PayPal',
  sepadirectdebit: 'SEPA',
  klarna: 'Klarna',
};

const METHOD_CLASSES: Record<string, string> = {
  scheme: 'border-blue-300/40 bg-blue-500/10 text-blue-700 dark:text-blue-300',
  googlepay: 'border-emerald-300/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  applepay: 'border-zinc-400/40 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300',
  ideal: 'border-pink-300/40 bg-pink-500/10 text-pink-700 dark:text-pink-300',
  paypal: 'border-amber-300/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  default: 'border-border bg-muted/40 text-muted-foreground',
};

function ExpandedDetail({ tx }: { tx: TransactionSummaryDto }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 text-xs md:grid-cols-2">
        <KV k="Correlation ID" v={tx.correlationId} mono />
        <KV k="Transaction ID" v={tx.id} mono />
        <KV k="Merchant ID" v={tx.merchantId ?? '—'} mono />
        <KV k="Store ID" v={tx.storeId ?? '—'} mono />
        <KV k="Payment method" v={tx.paymentMethodType ?? '—'} mono />
        <KV k="Error code" v={tx.errorCode ?? '—'} />
        <KV k="Created" v={new Date(tx.createdAt).toISOString()} mono />
      </div>
      <PayloadSection transactionId={tx.id} />
    </div>
  );
}

function PayloadSection({ transactionId }: { transactionId: string }) {
  const isAdmin = useHasRole('Admin');

  if (!isAdmin) {
    return (
      <div className="rounded-md border border-border bg-muted/20 p-3">
        <div className="flex items-center gap-2">
          <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Payload</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Request and response bodies are visible to Admins only.
        </p>
      </div>
    );
  }

  return <PayloadPanels transactionId={transactionId} />;
}

function PayloadPanels({ transactionId }: { transactionId: string }) {
  const query = useAdminTransactionsGetAdminTransactionsIdGuidPayload(transactionId);

  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Payload</span>
      </div>
      <PayloadBody query={query} />
    </div>
  );
}

type PayloadQuery = ReturnType<typeof useAdminTransactionsGetAdminTransactionsIdGuidPayload>;

function PayloadBody({ query }: { query: PayloadQuery }) {
  if (query.isLoading) {
    return <div className="text-xs text-muted-foreground">Loading payload…</div>;
  }
  if (query.isError) {
    const status = (query.error as { status?: number } | undefined)?.status;
    return (
      <div className="text-xs text-muted-foreground">
        Could not load payload{status ? ` (HTTP ${status})` : ''}.
      </div>
    );
  }
  const data = query.data as TransactionPayloadDto | undefined;
  if (!data) {
    return <div className="text-xs text-muted-foreground">No payload captured for this transaction.</div>;
  }

  const { requestBody, enrichedBody, responseBody, capturedAt } = data;
  const allNull = !requestBody && !enrichedBody && !responseBody;
  if (allNull) {
    return (
      <div className="text-xs text-muted-foreground">No payload captured for this transaction.</div>
    );
  }

  return (
    <div className="space-y-2">
      {capturedAt ? (
        <div className="text-[11px] text-muted-foreground">
          Captured at {new Date(capturedAt).toISOString()}
        </div>
      ) : null}
      {requestBody ? (
        <PayloadPanel label="Partner request" body={requestBody} defaultOpen />
      ) : null}
      {enrichedBody ? (
        <PayloadPanel label="Enriched body (sent to Adyen)" body={enrichedBody} />
      ) : null}
      {responseBody ? (
        <PayloadPanel label="Adyen response" body={responseBody} />
      ) : null}
    </div>
  );
}

function PayloadPanel({
  label,
  body,
  defaultOpen = false,
}: {
  label: string;
  body: string;
  defaultOpen?: boolean;
}) {
  const pretty = tryPrettyJson(body);
  const [copied, setCopied] = useState(false);
  return (
    <details
      open={defaultOpen}
      className="group rounded-md border border-border bg-background/40"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-2 text-xs">
        <span className="flex items-center gap-2">
          <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
          <span className="font-medium">{label}</span>
          <span className="text-[11px] text-muted-foreground">— {formatBytes(body.length)}</span>
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-2 text-[11px]"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void navigator.clipboard
              .writeText(body)
              .then(() => {
                setCopied(true);
                toast.success('Copied to clipboard.');
              })
              .catch(() => toast.error('Could not copy to clipboard.'));
          }}
        >
          <CopyIcon copied={copied} onRevert={() => setCopied(false)} className="h-3 w-3" />
          Copy
        </Button>
      </summary>
      <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
        {pretty}
      </pre>
    </details>
  );
}

function KV({ k, v, mono = false }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</span>
      <span className={cn(mono && 'font-mono')}>{v}</span>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' });
}
